import type { ThemeId } from './types';

export interface Ramp {
  light: string;
  dark: string;
}

export type PropKind =
  | 'redwood'
  | 'fern'
  | 'stump'
  | 'spire'
  | 'penguin'
  | 'igloo'
  | 'palm'
  | 'totem'
  | 'lavaFountain'
  | 'neonSign'
  | 'stall'
  | 'lamp'
  | 'asteroid'
  | 'satellite'
  | 'crystal'
  | 'balloon';

export interface PropSpec {
  kind: PropKind;
  weight: number;
  /** Normalised road-units from centre where this prop may sit (always outside 1.0). */
  minX: number;
  maxX: number;
  /** Base draw height as a fraction of road width. */
  scale: number;
  colors: string[];
}

export interface MusicSpec {
  /** Beats per minute of the theme's loop. */
  bpm: number;
  /** Semitone offsets from the root, one bar's worth of chord roots. */
  progression: number[];
  /** Scale used by the lead arpeggio. */
  scale: number[];
  /** Root note in Hz. */
  root: number;
  bass: 'sine' | 'triangle' | 'square' | 'sawtooth';
  lead: 'sine' | 'triangle' | 'square' | 'sawtooth';
  pad: 'sine' | 'triangle' | 'sawtooth';
  /** 16-step kick / snare / hat patterns. */
  kick: number[];
  snare: number[];
  hat: number[];
  /** 0..1 how much reverb-ish delay to use. */
  space: number;
}

export type AmbientKind = 'snow' | 'ash' | 'stars' | 'leaves' | 'confetti' | 'sparkle' | 'none';

export interface Theme {
  id: ThemeId;
  name: string;
  region: string;
  tagline: string;
  /** Order in the campaign; -1 means it is not part of the main progression. */
  order: number;
  sky: { top: string; mid: string; horizon: string };
  sun: { color: string; x: number; y: number; r: number } | null;
  fog: string;
  fogDensity: number;
  road: Ramp;
  rumble: Ramp;
  ground: Ramp;
  laneMark: string;
  /** Tint applied to distant geometry, gives each world its own air. */
  haze: string;
  accent: string;
  accent2: string;
  ambient: AmbientKind;
  /** Physics modifiers. */
  gravity: number;
  grip: number;
  speedMul: number;
  /** Track shape bias. */
  curviness: number;
  hilliness: number;
  /** Relative weights for the obstacle mix. */
  hazardWeights: Partial<Record<string, number>>;
  /** Relative weights for the shoulder shapes: gully, ridge line, bench cut. */
  terrain: { valley: number; spine: number; cut: number };
  /** Does the descent build staircases? */
  stairs: boolean;
  /** Dashed street markings, worn dirt ruts, or glowing energy edges. */
  laneStyle: 'dash' | 'rut' | 'glow';
  props: PropSpec[];
  music: MusicSpec;
  /** Trail length in segments for the designed run. */
  length: number;
  /** Difficulty ramp multiplier for the designed trail. */
  difficulty: number;
}

const MINOR = [0, 2, 3, 5, 7, 8, 10];
const DORIAN = [0, 2, 3, 5, 7, 9, 10];
const MAJOR = [0, 2, 4, 5, 7, 9, 11];
const PENTA_MINOR = [0, 3, 5, 7, 10];
const PHRYGIAN = [0, 1, 3, 5, 7, 8, 10];
const LYDIAN = [0, 2, 4, 6, 7, 9, 11];

export const THEMES: Record<ThemeId, Theme> = {
  redwood: {
    id: 'redwood',
    name: 'Ghost Ridge',
    region: 'Coastal Redwoods',
    tagline: 'Loam, fog, and a sun that will not wait for you.',
    order: 0,
    sky: { top: '#1b1236', mid: '#6a3164', horizon: '#ff9a52' },
    sun: { color: '#ffd08a', x: 0.62, y: 0.74, r: 0.16 },
    fog: '#6b4a72',
    fogDensity: 1.9,
    road: { light: '#7d5c41', dark: '#6d4f37' },
    rumble: { light: '#efe3c8', dark: '#9c4536' },
    ground: { light: '#3c6146', dark: '#2e4d36' },
    laneMark: '#f2e8cd',
    haze: '#4a2c5e',
    accent: '#ffb347',
    accent2: '#8be08b',
    ambient: 'leaves',
    gravity: 1,
    grip: 1,
    speedMul: 1,
    curviness: 0.9,
    hilliness: 1,
    hazardWeights: { rock: 3, log: 3, gate: 1.4, pit: 1, ramp: 2.4, traffic: 0.2, geyser: 0 },
    terrain: { valley: 2, spine: 0.8, cut: 2.4 },
    stairs: false,
    laneStyle: 'rut',
    props: [
      { kind: 'redwood', weight: 6, minX: 1.2, maxX: 3.4, scale: 2.9, colors: ['#4a3040', '#3c2634', '#563a48'] },
      { kind: 'fern', weight: 4, minX: 1.05, maxX: 1.9, scale: 0.42, colors: ['#3a7048', '#2c5a3a'] },
      { kind: 'stump', weight: 1.5, minX: 1.1, maxX: 2.2, scale: 0.5, colors: ['#5a4230', '#48331f'] },
    ],
    music: {
      bpm: 112,
      progression: [0, 0, -4, -2],
      scale: DORIAN,
      root: 110,
      bass: 'triangle',
      lead: 'triangle',
      pad: 'sawtooth',
      kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
      snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
      hat: [0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1],
      space: 0.45,
    },
    length: 4200,
    difficulty: 0.85,
  },

  antarctic: {
    id: 'antarctic',
    name: 'Aurora Shelf',
    region: 'Antarctic Ice',
    tagline: 'No grip, no brakes, and something is watching from the ice.',
    order: 1,
    sky: { top: '#02081c', mid: '#0a2246', horizon: '#1d6fa5' },
    sun: null,
    fog: '#2d5c80',
    fogDensity: 1.7,
    road: { light: '#d3e8f7', dark: '#bfd9ee' },
    rumble: { light: '#ffffff', dark: '#5fa8d8' },
    ground: { light: '#eaf5ff', dark: '#d7e8f8' },
    laneMark: '#7fbde6',
    haze: '#123a5c',
    accent: '#7cf7d8',
    accent2: '#b98bff',
    ambient: 'snow',
    gravity: 1,
    grip: 0.62,
    speedMul: 1.08,
    curviness: 0.7,
    hilliness: 1.15,
    hazardWeights: { rock: 2.4, log: 1, gate: 1.4, pit: 2.6, ramp: 2.6, traffic: 0.5, geyser: 0 },
    terrain: { valley: 1.6, spine: 1.6, cut: 1.8 },
    stairs: false,
    laneStyle: 'rut',
    props: [
      { kind: 'spire', weight: 5, minX: 1.15, maxX: 3.2, scale: 1.7, colors: ['#a9d6f2', '#8cc2e6', '#cfe9fb'] },
      { kind: 'penguin', weight: 2.2, minX: 1.08, maxX: 2.0, scale: 0.3, colors: ['#16202e', '#ffffff', '#ffbe3c'] },
      { kind: 'igloo', weight: 1, minX: 1.4, maxX: 2.6, scale: 0.6, colors: ['#e6f2fb', '#c3dcef'] },
    ],
    music: {
      bpm: 126,
      progression: [0, 3, -2, -4],
      scale: MINOR,
      root: 98,
      bass: 'sine',
      lead: 'sine',
      pad: 'sawtooth',
      kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0],
      snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
      hat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      space: 0.7,
    },
    length: 4600,
    difficulty: 1.15,
  },

  volcano: {
    id: 'volcano',
    name: 'Ember Atoll',
    region: 'Volcanic Tropics',
    tagline: 'The island is on fire. The island is always on fire.',
    order: 2,
    sky: { top: '#1a0710', mid: '#6b1a0d', horizon: '#ff8a34' },
    sun: { color: '#ffd25e', x: 0.38, y: 0.79, r: 0.12 },
    fog: '#7d3a1f',
    fogDensity: 2.3,
    road: { light: '#3b3239', dark: '#332b31' },
    rumble: { light: '#ff8f3c', dark: '#8a2a12' },
    ground: { light: '#12403c', dark: '#0d3230' },
    laneMark: '#ffbf7a',
    haze: '#6b2410',
    accent: '#ff6a3d',
    accent2: '#3ee0c8',
    ambient: 'ash',
    gravity: 1.04,
    grip: 1.05,
    speedMul: 1.12,
    curviness: 1.25,
    hilliness: 1.35,
    hazardWeights: { rock: 2.2, log: 1.2, gate: 1, pit: 1.4, ramp: 2.8, traffic: 0.3, geyser: 3.2 },
    terrain: { valley: 2.6, spine: 1.2, cut: 1.8 },
    stairs: false,
    laneStyle: 'rut',
    props: [
      { kind: 'palm', weight: 5, minX: 1.12, maxX: 3.0, scale: 1.5, colors: ['#1d3a2c', '#2b5140', '#3a2a1c'] },
      { kind: 'totem', weight: 1.6, minX: 1.1, maxX: 2.0, scale: 0.8, colors: ['#4a2c1e', '#ff7a3c', '#f5e0c0'] },
      { kind: 'lavaFountain', weight: 2.4, minX: 1.6, maxX: 3.6, scale: 1.1, colors: ['#ff4d1a', '#ffd24d', '#8a1c06'] },
    ],
    music: {
      bpm: 138,
      progression: [0, -3, 2, -1],
      scale: PHRYGIAN,
      root: 116,
      bass: 'sawtooth',
      lead: 'square',
      pad: 'sawtooth',
      kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0],
      snare: [0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0],
      hat: [1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 1],
      space: 0.3,
    },
    length: 4900,
    difficulty: 1.3,
  },

  metro: {
    id: 'metro',
    name: 'Neón Mercado',
    region: 'Rooftop City',
    tagline: 'Downhill through the market, and the market is not slowing down.',
    order: 3,
    sky: { top: '#0a0620', mid: '#241243', horizon: '#7d2266' },
    sun: null,
    fog: '#42265e',
    fogDensity: 2,
    road: { light: '#2d2d3b', dark: '#262631' },
    rumble: { light: '#ff2e88', dark: '#00d9f5' },
    ground: { light: '#3c2b4e', dark: '#332442' },
    laneMark: '#f7efb5',
    haze: '#3a1b52',
    accent: '#ff2e88',
    accent2: '#00e5ff',
    ambient: 'confetti',
    gravity: 1,
    grip: 1.12,
    speedMul: 1.16,
    curviness: 1.5,
    hilliness: 0.8,
    hazardWeights: { rock: 1.2, log: 1, gate: 2.2, pit: 1.2, ramp: 3.2, traffic: 4, geyser: 0 },
    terrain: { valley: 0.8, spine: 0.7, cut: 1.6 },
    stairs: true,
    laneStyle: 'dash',
    props: [
      { kind: 'neonSign', weight: 4, minX: 1.15, maxX: 2.6, scale: 1.4, colors: ['#ff2e88', '#00e5ff', '#ffe14d', '#7cff6b'] },
      { kind: 'stall', weight: 3, minX: 1.08, maxX: 2.1, scale: 0.55, colors: ['#ff5a3c', '#ffd24d', '#2b2b3a'] },
      { kind: 'lamp', weight: 2.4, minX: 1.1, maxX: 1.6, scale: 1.1, colors: ['#2b2b3a', '#ffe9a8'] },
    ],
    music: {
      bpm: 146,
      progression: [0, 5, 3, -2],
      scale: PENTA_MINOR,
      root: 130.81,
      bass: 'sawtooth',
      lead: 'square',
      pad: 'sawtooth',
      kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
      snare: [0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0, 1],
      hat: [1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
      space: 0.25,
    },
    length: 5200,
    difficulty: 1.45,
  },

  belt: {
    id: 'belt',
    name: 'Belt Runner',
    region: 'The Asteroid Belt',
    tagline: 'Gravity took the night off. You did not.',
    order: 4,
    sky: { top: '#01020c', mid: '#080a24', horizon: '#3b1f6e' },
    sun: null,
    fog: '#24245a',
    fogDensity: 1.3,
    road: { light: '#2b2454', dark: '#241e49' },
    rumble: { light: '#7cf7ff', dark: '#a05cff' },
    ground: { light: '#05061a', dark: '#03040f' },
    laneMark: '#7cf7ff',
    haze: '#1b1145',
    accent: '#7cf7ff',
    accent2: '#ff5cf0',
    ambient: 'stars',
    gravity: 0.44,
    grip: 0.86,
    speedMul: 1.24,
    curviness: 1.1,
    hilliness: 1.6,
    hazardWeights: { rock: 3, log: 0.6, gate: 1.6, pit: 3.4, ramp: 3.4, traffic: 1.2, geyser: 0.6 },
    terrain: { valley: 0.4, spine: 3.2, cut: 1.2 },
    stairs: false,
    laneStyle: 'glow',
    props: [
      { kind: 'asteroid', weight: 5, minX: 1.2, maxX: 4.0, scale: 1.2, colors: ['#3a3350', '#2a2440', '#4a4266'] },
      { kind: 'satellite', weight: 1.4, minX: 1.6, maxX: 3.4, scale: 0.9, colors: ['#c9d4ff', '#7cf7ff', '#3a3350'] },
      { kind: 'crystal', weight: 2.6, minX: 1.1, maxX: 2.4, scale: 0.9, colors: ['#7cf7ff', '#a05cff', '#ff5cf0'] },
    ],
    music: {
      bpm: 132,
      progression: [0, 4, 2, 7],
      scale: LYDIAN,
      root: 87.31,
      bass: 'sine',
      lead: 'triangle',
      pad: 'sawtooth',
      kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 1],
      snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
      hat: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
      space: 0.85,
    },
    length: 5200,
    difficulty: 1.55,
  },

  chroma: {
    id: 'chroma',
    name: 'Chroma Void',
    region: 'Nowhere In Particular',
    tagline: 'It is not a place. It is a rumour with a fall line.',
    order: 5,
    sky: { top: '#12042a', mid: '#3c0f6b', horizon: '#ff4fa3' },
    sun: { color: '#fff3a8', x: 0.5, y: 0.8, r: 0.1 },
    fog: '#5f2d84',
    fogDensity: 1.6,
    road: { light: '#3a2b5e', dark: '#332552' },
    rumble: { light: '#ffe14d', dark: '#ff4fa3' },
    ground: { light: '#1c0a38', dark: '#15062c' },
    laneMark: '#ffffff',
    haze: '#42106e',
    accent: '#ffe14d',
    accent2: '#4dffe1',
    ambient: 'sparkle',
    gravity: 0.82,
    grip: 1,
    speedMul: 1.3,
    curviness: 1.7,
    hilliness: 1.5,
    hazardWeights: { rock: 2, log: 1.4, gate: 2, pit: 2, ramp: 3.2, traffic: 1.6, geyser: 1.4 },
    terrain: { valley: 1.2, spine: 2.4, cut: 1.6 },
    stairs: true,
    laneStyle: 'glow',
    props: [
      { kind: 'crystal', weight: 4, minX: 1.1, maxX: 3.0, scale: 1.1, colors: ['#ffe14d', '#4dffe1', '#ff4fa3'] },
      { kind: 'balloon', weight: 3, minX: 1.2, maxX: 3.4, scale: 0.8, colors: ['#ff4fa3', '#4dffe1', '#ffe14d', '#8b5cff'] },
      { kind: 'asteroid', weight: 1.6, minX: 1.4, maxX: 3.6, scale: 1, colors: ['#4a2a70', '#3a2060'] },
    ],
    music: {
      bpm: 152,
      progression: [0, 2, 5, 9],
      scale: MAJOR,
      root: 146.83,
      bass: 'sawtooth',
      lead: 'square',
      pad: 'sawtooth',
      kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 0],
      snare: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 1],
      hat: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      space: 0.55,
    },
    length: 5600,
    difficulty: 1.75,
  },
};

/** Campaign order, excluding bonus worlds. */
export const CAMPAIGN: ThemeId[] = (Object.values(THEMES) as Theme[])
  .filter((t) => t.order >= 0)
  .sort((a, b) => a.order - b.order)
  .map((t) => t.id);

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];

export function getTheme(id: ThemeId): Theme {
  return THEMES[id] ?? THEMES.redwood;
}

export function isThemeId(v: unknown): v is ThemeId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(THEMES, v);
}
